'use strict';
//***********************************************************************************
//***********************************************************************************
//**** cn_camera : a 2D camera
//***********************************************************************************
//***********************************************************************************

import { fh_polygon, fh_solid } from '@acenv/fh-3d-viewer';
import {
    cn_add,
    cn_cart,
    cn_clone,
    cn_dist,
    cn_dot,
    cn_manhattan_distance,
    cn_middle,
    cn_mul,
    cn_normal,
    cn_normalize,
    cn_polar,
    cn_sub,
    cn_uuid,
    cnx_add,
    cnx_build_axis,
    cnx_dist,
    cnx_mul
} from '../utils/cn_utilities';
import { cn_global_params } from '../utils/cn_global_params';
import { cn_image_dir } from '../utils/image_dir';
import { cn_svg_context } from './cn_svg_context';

var SNAP_RATIO = 0.005;
var _NB_CAMERAS = 0;

export class cn_angle_sensitive_area {
    constructor() {
        this.screen = [0, 0, 0];
        this.mouseover = false;
        this.selected = false;
        this.angles = [0, 0];
    }

    on_point(screen_point) {
        return cn_dist(screen_point, this.screen) < this.screen[2];
    }
}

export class cn_camera {
    constructor() {
        var obj = this;

        this.ID = cn_uuid('camera' + _NB_CAMERAS);
        _NB_CAMERAS++;

        //*** 0: world x of screen center
        //*** 1: world y of screen center
        //*** 2: world length of screen diagonal
        this._matrix = [0, 0, 30];
        this._width = 100;
        this._height = 100;
        this._screen_to_world = [0, 0];
        this._world_to_screen = [0, 0];

        this.snap_threshold = 0.01;

        this._grid_min_step = 1;

        this.screen_to_world_scale = 0;
        this.world_to_screen_scale = 0;

        this.padding = [0, 0, 0, 0];
        this.show_angles = true;
        this.show_measures = true;
        this.show_space_measure = true;
        this.show_wall_type = false;
        this.show_facings = false;
        this.use_grid = true;
        this.draw_objects_icon = true;
        this.draw_objects_top_view = true;


        this.snap_world_distance = 0;
        this.snap_screen_distance = 0;

        this.thumb_size = 40;

        this.element_filter = null;

        this.storey = null;

        this.background_mode = false;

        /**
         * Minimum world length to show a measure (method draw_measure).
         */
        this.measure_limit = 0;

        this.scales = {};

        this._svg_context = new cn_svg_context();

        this._patterns = {};
    }

    /**
     * Does the camera uses 3D (true for derivate such as cn_overlay_camera)
     * @returns {boolean}
     */
    is_3d() {
        return false;
    }

    /**
     * Sets scale for a given field
     * @param {string} param
     * @param {number} value
     */
    set_scale(param, value) {
        this.scales[param] = value;
    }

    /**
     * Returns scale for a given field
     * @param {string} param
     * @returns {number}
     */
    get_scale(param) {
        return (this.scales[param] || 1);
    }

    /**
     * Returns the camera's svg context
     * @return {cn_svg_context}
     */
    get_svg_context() {
        return this._svg_context;
    }

    //***********************************************************************************
    //**** Sets padding for camera center
    //***********************************************************************************
    set_padding(top = 0, right = 0, bottom = 0, left = 0) {
        this.padding[0] = top;
        this.padding[1] = right;
        this.padding[2] = bottom;
        this.padding[3] = left;
    }

    //***********************************************************************************
    //**** Set size
    //***********************************************************************************
    set_size(width, height) {
        if (this._width === width && this._height === height) return;
        this._width = width;
        this._height = height;
        this._update_matrices();
    }

    get_screen_size() {
        return [this._width, this._height];
    }

    get_world_size() {
        return [this._width * this.screen_to_world_scale, this._height * this.screen_to_world_scale];
    }

    //***********************************************************************************
    //**** Camera wrold (world center of camera)
    //***********************************************************************************
    get_actual_padding() {
        var pp = this.padding.concat([]);
        if (this._width - this.padding[1] - this.padding[3] < 10) {
            pp[1] = 0;
            pp[3] = 0;
        }
        if (this._height - this.padding[0] - this.padding[2] < 10) {
            pp[0] = 0;
            pp[2] = 0;
        }
        return pp;
    }

    get_world_center() {
        var padding = this.get_actual_padding();
        return this.screen_to_world([padding[3] + (this._width - padding[1] - padding[3]) / 2, padding[0] + (this._height - padding[2] - padding[0]) / 2]);
    }

    get_world_height() {
        var padding = this.get_actual_padding();
        return (this._height - padding[2] - padding[0]) * this.screen_to_world_scale;
    }

    set_world_focus(world_center, world_height) {
        var padding = this.get_actual_padding();
        var height = this._height - padding[0] - padding[2];
        var screen_to_world = world_height / height;
        this._matrix[0] = world_center[0] + (padding[1] - padding[3]) * screen_to_world * 0.5;
        this._matrix[1] = world_center[1] - (padding[2] - padding[0]) * screen_to_world * 0.5;
        var diagonal = Math.sqrt(this._width * this._width + this._height * this._height);
        if (diagonal < 1) diagonal = 1;
        this._matrix[2] = screen_to_world * diagonal;
        this._update_matrices();
    }

    //***********************************************************************************
    //**** Fit camera to given box
    //***********************************************************************************
    fit_box(box) {
        if (!box.posmin) return;
        var padding = this.get_actual_padding();

        var width = this._width - padding[1] - padding[3];
        var height = this._height - padding[0] - padding[2];

        var scale0 = (1 + box.size[1]) / height;
        var scale1 = (1 + box.size[0]) / width;
        var scale = (scale0 > scale1) ? scale0 : scale1;
        var center = cn_add(box.posmin, cn_mul(box.size, 0.5));
        this.set_world_focus(center, scale * height);
    }

    //***********************************************************************************
    //**** Copy
    //***********************************************************************************
    copy(other_camera) {
        for (var k = 0; k < 3; k++)
            this._matrix[k] = other_camera._matrix[k];
        this._update_matrices();
    }

    //***********************************************************************************
    //**** Coordinates
    //***********************************************************************************

    /**
     * Screen to world projection
     * @param {number[]} src
     * @returns {number[]}
     */
    screen_to_world(src) {
        return [this._screen_to_world[0] + src[0] * this.screen_to_world_scale, this._screen_to_world[1] + (this._height - src[1]) * this.screen_to_world_scale];
    }

    /**
     * World to screen projection
     * @param {number[]} src
     * @returns {number[]}
     */
    world_to_screen(src, z = 0) {
        return [this._world_to_screen[0] + src[0] * this.world_to_screen_scale, this._height - this._world_to_screen[1] - src[1] * this.world_to_screen_scale];
    }


    /**
     * Checks if a world point is visible on screen (only for 3D camera)
     * @param {number[]} point
     * @returns {boolean}
     */
    check_visibility(point) {
        return true;
    }

    //***********************************************************************************
    //**** Draw the grid
    //***********************************************************************************
    draw_grid(min_spacing = 0, extra_classes = []) {
        var html = '';
        var p0 = this.screen_to_world([0, this._height]);
        var p1 = this.screen_to_world([this._width, 0]);

        const ms = cn_global_params.min_grid_spacing || min_spacing;

        var threshold = (ms) ? ms / 2 : 0.005;
        const scales = [];
        for (var scale = 10; scale > threshold; scale *= 0.1) {
            if (ms === 0 && scale * this.world_to_screen_scale < 20) break;
            scales.push(scale);
        }
        if (scales.length == 0) scales.push(10);
        if (ms && scales[scales.length - 1] > ms) scales.push(ms);

        var nscale = 0;
        scales.forEach(scale => {
            this._grid_min_step = scale;
            for (var x = scale * Math.floor(p0[0] / scale); x < p1[0]; x += scale) {
                var xx = this._world_to_screen[0] + x * this.world_to_screen_scale;
                var extra = (Math.abs(x) < scale / 2) ? ' grid_origin' : '';
                if (extra_classes.length > 0) extra += ' ' + extra_classes.join(' ');
                html += '<line class=\'grid_' + nscale + extra + '\' x1=\'' + xx + '\' y1=\'0\' x2=\'' + xx + '\' y2=\'' + this._height + '\' />';
            }
            for (var y = scale * Math.floor(p0[1] / scale); y < p1[1]; y += scale) {
                var yy = this._height - this._world_to_screen[1] - y * this.world_to_screen_scale;
                var extra = (Math.abs(y) < scale / 2) ? ' grid_origin' : '';
                if (extra_classes.length > 0) extra += ' ' + extra_classes.join(' ');
                html += '<line class=\'grid_' + nscale + extra + '\' x1=\'0\' y1=\'' + yy + '\' x2=\'' + this._width + '\' y2=\'' + yy + '\' />';
            }
            nscale++;
        });
        return html;

    }

    draw_background() {
        let html = '';
        html += `<rect x="0" y="0" width="100%" height="100%" class="map_background"></rect>`
        return html;
    }

    //***********************************************************************************
    //**** Draw the scale
    //***********************************************************************************
    draw_scale(min_spacing = 1, extra_classes = []) {
        const ms = cn_global_params.min_grid_spacing || min_spacing || 1;
        var lengths = [1, 2, 5, 10, 20, 50, 100].map(length => length * ms);
        var length = lengths[0];
        for (var i = 0; i < lengths.length; i++) {
            length = lengths[i];
            if (length * this.world_to_screen_scale > 10) break;
        }
        var sw = length * this.world_to_screen_scale;
        var html = '';
        var extra = extra_classes.join(' ');
        html += '<rect class=\'scale_background ' + extra + '\' x=\'' + (this._width - 30 - sw) + '\' y=\'' + (this._height - 40) + '\' width=\'' + (20 + sw) + '\' height=\'30\' />';
        html += '<line class=\'scale_line ' + extra + '\' x1=\'' + (this._width - 20) + '\' y1=\'' + (this._height - 20) + '\' x2=\'' + (this._width - 20 - sw) + '\' y2=\'' + (this._height - 20) + '\' />';
        html += '<line class=\'scale_end_line ' + extra + '\' x1=\'' + (this._width - 20) + '\' y1=\'' + (this._height - 25) + '\' x2=\'' + (this._width - 20) + '\' y2=\'' + (this._height - 15) + '\' />';
        html += '<line class=\'scale_end_line ' + extra + '\' x1=\'' + (this._width - 20 - sw) + '\' y1=\'' + (this._height - 25) + '\' x2=\'' + (this._width - 20 - sw) + '\' y2=\'' + (this._height - 15) + '\' />';
        html += '<text class=\'scale_text ' + extra + '\' x=\'' + (this._width - 20 - sw * 0.5) + '\' y=\'' + (this._height - 30) + '\'>' + Math.round(length * 10) / 10 + ' m</text>';
        return html;
    }

    //***********************************************************************************
    //**** Draw simple Legend
    //***********************************************************************************
    draw_simple_legend(elements, title, wall_types) {
        let html = ''
        let y_start = 0
        let y = 14;
        const step = 10;
        const x_start = 5
        let legend_title = [];
        y += step + 5;
        if (elements.length) {
            legend_title.push(title)
            const legend_details = this._draw_legend_entry(elements, x_start + 5, y, step, false)
            html += legend_details.html;
            y = legend_details.adjusted_y;
        }
        if (this.show_wall_type) {
            legend_title.push('des types de mur');
            const wall_types_legend = this._draw_wall_types_entries(wall_types, x_start, y, step);
            html += wall_types_legend.html;
            y = wall_types_legend.adjusted_y;
        }
        if (elements.length || this.show_wall_type) {
            y += step;
            html = `<text class="legend_title exp" x="${x_start}" y="14">Légende ${legend_title.join(' et ')}</text>` + html;
            html = `<rect class='legend_background exp' x="0" y="${y_start}" width="250"
                height="${y - y_start}" />` + html
        }
        return html;
    }

    //***********************************************************************************
    //**** Draw advanced legend
    //***********************************************************************************
    draw_advanced_legend(zpso_legend, sampling_legend, other_elements_legend, wall_types, title_bloc_specs) {
        let html = '';
        const step = 10;
        const title = (title_bloc_specs.level || '') + (title_bloc_specs.title ? ' - ' + title_bloc_specs.title : '');
        const auteur = title_bloc_specs.auteur || '';
        const numDossier = title_bloc_specs.numeroDossier || '';
        const adresse = title_bloc_specs.adresse || '';
        const indice = title_bloc_specs.indice || '';
        const planche = title_bloc_specs.planche || '';
        const logo = title_bloc_specs.logo || '';
        let y_start = 0
        let y = 14;
        let x_start_title = 5;
        let new_line_regex = /.{1,40}( |$)/g;
        if (logo) {
            x_start_title = 52;
            new_line_regex = /.{1,30}( |$)/g;
            html += `<image xlink:href="${logo}" x="2" y="2" height="48" width="48"/>`
        }
        if (title) {
            title.match(new_line_regex).forEach((span, index, allEntries) => {
                if (index === 0) {
                    html += `<text class="legend_title exp" x="${x_start_title}" y="${y}">`;
                }
                html += `<tspan x="${x_start_title}" y="${y}">${span}</tspan>`;
                y += step + 5;
                if (index === allEntries.length - 1) {
                    html += '</text>';
                }
            });
        }
        y -= 5;
        y = Math.max(y, 50);
        html = `<rect class="legend_title_block exp" x="0" y="${y_start}" width="250" height="${y - y_start}"></rect>` + html;
        y += step + 3;
        if (auteur) {
            html += `<text class="legend_text exp" x="5" y="${y}">Auteur : ${auteur}</text>`;
            y += step + 3;
        }
        if (numDossier) {
            html += `<text class="legend_text exp" x="5" y="${y}">Dossier n° : ${numDossier}</text>`;
            y += step + 3;
        }
        if (adresse) {
            const address = `Adresse du bien : ${adresse}`;
            if (address) {
                address.match(/.{1,48}( |$)/g).forEach((span, index, allEntries) => {
                    if (index === 0) {
                        html += `<text class="legend_text exp" x="5" y="${y}">`;
                    }
                    html += `<tspan x="5" y="${y}">${span}</tspan>`;
                    y += step + 3;
                    if (index === allEntries.length - 1) {
                        html += '</text>';
                    }
                });
            }
        }
        if (indice) {
            html += `<text class="legend_text exp" x="5" y="${y}">Indice : ${indice}, (${planche})</text>`;
            y += 5;
        } else {
            html += `<text class="legend_text exp" x="5" y="${y}">Planche : ${planche}</text>`;
            y += 5;
        }
        html = `<rect class="legend_background exp" x="0" y="${y_start}" width="250" height="${y - y_start}"></rect>` + html;
        if (zpso_legend.length || sampling_legend.length || other_elements_legend.elements.length || this.show_wall_type) {
            const x = 10;
            y_start = y;
            y += step * 2;
            html += `<text class="legend_title exp" x="${x}" y="${y}">Légende</text>`
            y += step * 2;
            if (zpso_legend.length) {
                const zpso_amianted = zpso_legend.filter(zpso => zpso.control_result === 'Amianté');
                const zpso_not_amianted = zpso_legend.filter(zpso => zpso.control_result === 'Non amianté');
                const zpso_potential = zpso_legend.filter(zpso => !zpso.control_result);
                if (zpso_amianted.length) {
                    html += `<text class="legend_text exp" x="${x}" y="${y}">Matériaux contenant de l'amiante</text>`;
                    y += step;
                    const zpso_entry = this._draw_legend_entry(zpso_amianted, x, y, step);
                    html += zpso_entry.html;
                    y = zpso_entry.adjusted_y
                    y += step;
                }
                if (zpso_not_amianted.length) {
                    html += `<text class="legend_text exp" x="${x}" y="${y}">Matériaux ne contenant pas d'amiante</text>`;
                    y += step;
                    const zpso_entry = this._draw_legend_entry(zpso_not_amianted, x, y, step);
                    html += zpso_entry.html;
                    y = zpso_entry.adjusted_y
                    y += step;
                }
                if (zpso_potential.length) {
                    html += `<text class="legend_text exp" x="${x}" y="${y}">Matériaux susceptibles de contenir de l'amiante</text>`;
                    y += step;
                    const zpso_entry = this._draw_legend_entry(zpso_potential, x, y, step);
                    html += zpso_entry.html;
                    y = zpso_entry.adjusted_y
                    y += step;
                }
            } else if (other_elements_legend.elements.length) {
                html += `<text class="legend_text exp" x="${x}" y="${y}">${other_elements_legend.title}</text>`;
                y += step;
                const legend_entry = this._draw_legend_entry(other_elements_legend.elements, x, y, step, false)
                html += legend_entry.html;
                y = legend_entry.adjusted_y;
                y += step;
            }
            if (sampling_legend.length) {
                html += `<text class="legend_text exp" x="${x}" y="${y}">Prélèvements</text>`;
                y += step;
                html += `<rect width="30px" height="20px" class="sampling_amianted_sampler" x="${x}" y="${y}"></rect>`;
                html += `<text class="legend_entry_text" x="${x + 35}" y="${y + 8}">Avec</text>`;
                html += `<text class="legend_entry_text" x="${x + 35}" y="${y + 19}">amiante</text>`;
                html += `<rect width="30px" height="20px" class="sampling_not_amianted_sampler" x="${x + 80}" y="${y}"></rect>`;
                html += `<text class="legend_entry_text" x="${x + 115}" y="${y + 8}">Sans</text>`;
                html += `<text class="legend_entry_text" x="${x + 115}" y="${y + 19}">amiante</text>`;
                html += `<rect width="30px" height="20px" class="sampling_no_result_sampler" x="${x + 160}" y="${y}"></rect>`;
                html += `<text class="legend_entry_text" x="${x + 195}" y="${y + 8}">En</text>`;
                html += `<text class="legend_entry_text" x="${x + 195}" y="${y + 19}">attente</text>`;
                y += 20 + step;
                html += `<circle class="exp marker_sampler" cx="${x + 10}" cy="${y + 10}" r="10"></circle>`;
                html += `<text class="legend_entry_text" x="${x + 25}" y="${y + 14}">Au plafond</text>`;
                html += `<rect class="exp marker_sampler" x="${x + 120}" y="${y}" width="20" height="20"></rect>`;
                html += `<text class="legend_entry_text" x="${x + 145}" y="${y + 14}">Au sol</text>`;
                y += 20 + step;
                html += `<path class="exp marker_sampler" d="M ${x} ${y + 10}
                L ${x + 10} ${y + 10} ${x + 10} ${y} ${x + 20} ${y + 10} ${x + 10} ${y + 20} ${x + 10} ${y + 10} Z" />`;
                html += `<text class="legend_entry_text" x="${x + 25}" y="${y + 14}">Sur mur</text>`;
                html += `<path class="exp marker_sampler" d="M ${x + 120} ${y}
                L ${x + 140} ${y + 20} M ${x + 120} ${y + 20} L ${x + 140} ${y}" />`;
                html += `<text class="legend_entry_text" x="${x + 145}" y="${y + 14}">Sur autre type</text>`;
                y += 20 + step;
            }
            if (this.show_wall_type) {
                html += `<text class="legend_text exp" x="${x}" y="${y}">Types de mur</text>`;
                y += step;
                const wall_types_legend = this._draw_wall_types_entries(wall_types, x, y, step);
                html += wall_types_legend.html;
                y = wall_types_legend.adjusted_y;
            }
            y += step;
            html = `<rect class='legend_background exp' x="0" y="${y_start}" width="250"
            height="${y - y_start}" />` + html;
        }
        return html;
    }

    _draw_legend_entry(layer_entries, x, y, step, description = true) {
        let html = '';
        let adjusted_y = y;
        layer_entries.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true })).forEach(layer => {
            let fill = '';
            if (!layer.ref_id) {
                const rotation = layer.stripes === 'vertical' ? 0 : layer.stripes === 'diagonal' ? -45 : 90;
                html += `<pattern id="${layer.ID}" width="10" height="10" patternUnits="userSpaceOnUse"
                        patternTransform="rotate(${rotation})">
                        <rect width="2" height="10" fill="${layer.color}"></rect>
                    </pattern>`;
                fill = layer.stripes !== 'full' ? 'url(#' + layer.ID + ')' : layer.color;
            } else {
                fill = 'url(#' + layer.ref_id + ')'
            }
            html += `<rect width="40px" height="20px" x="${x}" y="${adjusted_y}" fill="${fill}"></rect>`;

            html += `<line x1="${x}" y1="${adjusted_y + 28}" x2="${x + 40}" y2="${adjusted_y + 28}" stroke="${layer.color}" stroke-width="5" stroke-dasharray="${layer.dasharray}" ></line>`;

            let name = layer.name;
            if (description) name += ' - ' + layer.description;
            if (name.length > 40) {
                html += `<text class="legend_entry_text" x="${x + 60}" y="${adjusted_y}">`;
                let [_, first, second] = name.match(/(.{1,40}) (.{1,40})/);
                html += `<tspan x="${x + 60}" y="${adjusted_y + 8}">${first}</tspan>`;
                if (second.length === 40) {
                    second = second.slice(0, -3) + '...';
                }
                html += `<tspan x="${x + 60}" y="${adjusted_y + 19}">${second}</tspan>`;
                html += `</text>`;
            } else {
                html += `<text class="legend_entry_text" x="${x + 60}" y="${adjusted_y + 14}">${name}</text>`;
            }
            adjusted_y += 30 + step;
        });
        return { html, adjusted_y };
    }

    _draw_wall_types_entries(wall_types, x, y, step) {
        let html = '';
        let adjusted_y = y;
        wall_types.forEach(wt => {
            html += `<svg x="${x}" y="${adjusted_y}" style="width: 50px; height: 20px;" >
                     ${wt.draw_svg_icon(50, 20)}
                    </svg>`;
            let name = wt.get_label();
            if (name.length > 40) {
                html += `<text class="legend_entry_text" x="${x + 60}" y="${adjusted_y}">`;
                let [_, first, second] = name.match(/(.{1,40}) (.{1,40})/);
                html += `<tspan x="${x + 60}" y="${adjusted_y + 8}">${first}</tspan>`;
                if (second.length === 40) {
                    second = second.slice(0, -3) + '...';
                }
                html += `<tspan x="${x + 60}" y="${adjusted_y + 19}">${second}</tspan>`;
                html += `</text>`;
            } else {
                html += `<text class="legend_entry_text" x="${x + 60}" y="${adjusted_y + 14}">${name}</text>`;
            }
            adjusted_y += 20 + step;
        })
        return { html, adjusted_y };
    }

    //***********************************************************************************
    //**** Draw the compass
    //***********************************************************************************
    draw_compass(orientation, mouseover = false) {
        let html = ``;
        const corner = this._compass_corner();
        if (mouseover) {
            const radius = this._compass_radius();
            const center = cn_add(corner, [radius, radius]);
            html += `<circle cx="${center[0]}" cy="${center[1]}" r="${radius}" style="stroke: none; fill: rgb(255,255,0); opacity: 0.5" />`;
        }
        html += `<svg x="${corner[0]}" y="${corner[1]}" width="60" height="60" viewBox="0 0 100 100">`;
        html += `<g transform="rotate(${-180.5 + orientation} 50 50) scale(0.01)" fill="#000000" stroke="none">
<path d="M4560 9789 c-1035 -75 -1998 -463 -2800 -1128 -150 -123 -501 -475 -622 -621 -881 -1068 -1270 -2403 -1098
-3765 119 -934 501 -1801 1124 -2545 98 -117 370 -393 496 -504 1310 -1150 3104 -1521 4760 -984 1113 361 2076 1129
2686 2144 503 838 746 1840 684 2819 -70 1094 -491 2109 -1216 2935 -112 128 -386 398 -509 501 -762 642 -1665 1026
-2665 1134 -174 19 -664 27 -840 14z m761 -455 c1240 -117 2371 -748 3128 -1744 424 -557 710 -1208 835 -1900 52 -286
61 -399 61 -790 0 -391 -9 -504 -61 -790 -141 -778 -483 -1499 -999 -2105 -183 -215 -476 -491 -697 -657 -646 -484 -1361
-772 -2168 -874 -170 -21 -683 -30 -865 -15 -380 32 -701 96 -1055 212 -205 67 -343 125 -575 240 -434 214 -782 461 -1128
799 -394 384 -674 772 -906 1252 -237 490 -375 983 -425 1517 -21 217 -21 625 0 842 118 1247 751 2377 1762 3142 629 477
1433 792 2207 867 61 5 124 12 140 14 96 11 598 4 746 -10z"
style="stroke-width: 0; stroke: rgb(186, 218, 85); fill: rgb(100, 100, 100);">
</path>
<path d="M 3327 5687 L 4113 4113 L 5687 3327 L 7260 2540 L 6473 4114 L 5685 5688 L 4113 6474 L 2540 7260 L 3327 5687 Z M
4310 5920 C 4868 5600 5327 5336 5329 5334 C 5334 5330 4480 4460 4471 4460 C 4466 4460 3338 6384 3281 6488 C 3270 6509 3270
6512 3282 6508 C 3289 6505 3752 6240 4310 5920 Z"
style="paint-order: fill; stroke-miterlimit: 17; stroke-width: 0; stroke: rgb(186, 218, 85);"
transform="matrix(-0.71934, 0.694658, -0.694658, -0.71934, 11828.591045, 5020.93852)">
</path>
<path d="M 529.541 -602.23 L 1649.54 2737.764 L -590.458 2737.764 L 529.541 -602.23 Z"
data-bx-shape="triangle -590.458 -602.23 2239.998 3339.994 0.5 0 1@2bca6eb4"
style="stroke: rgb(0, 0, 0); fill: rgb(255, 0, 0);"
transform="matrix(0.999848, 0.01745, 0.01745, -0.999848, 4322.733101, 7628.084939)">
</path>
</g>
</svg>`;
        return html;
    }

    mouseover_compass(screen_position) {
        return cn_dist(this._compass_center(), screen_position) < this._compass_radius();
    }

    _compass_corner() {
        return [this._width - 90, this._height - 120];
    }

    _compass_radius() {
        return 30;
    }

    _compass_center() {
        return cn_add(this._compass_corner(), [this._compass_radius(), this._compass_radius()]);
    }

    //***********************************************************************************
    //**** Snap methods
    //***********************************************************************************
    snap_world(p) {
        if (!this.use_grid) return cn_clone(p);
        var x = this._grid_min_step * Math.round(p[0] / this._grid_min_step);
        if (Math.abs(x - p[0]) > this.snap_world_distance)
            x = p[0];
        var y = this._grid_min_step * Math.round(p[1] / this._grid_min_step);
        if (Math.abs(y - p[1]) > this.snap_world_distance)
            y = p[1];
        return [x, y];
    }

    snap_screen(p) {
        return this.world_to_screen(this.snap_world(this.screen_to_world(p)));
    }

    snap_edge(p, edge0, edge1, previous_point = null) {

        var res = {};
        res.snap_grid = [false, false];
        res.snap_vertex = -1;
        res.snap_previous = false;

        var snap = false;

        var direction = cn_sub(edge1, edge0);
        var length = cn_normalize(direction);
        var z0 = cn_dot(cn_sub(p, edge0), direction);
        res.point = cn_add(edge0, cn_mul(direction, z0));

        //*** special case if snap on an edge vertex
        if (z0 <= this.snap_world_distance) {
            res.point = [edge0[0], edge0[1]];
            res.snap_vertex = 0;
            res.position = 0;
            return res;
        }
        if (z0 >= length - this.snap_world_distance) {
            res.point = [edge1[0], edge1[1]];
            res.snap_vertex = 1;
            res.position = length;
            return res;
        }

        res.snap_vertex = -1;
        var min_distance = this.snap_world_distance;
        for (var k = 0; k < 2; k++) {
            if (Math.abs(direction[k]) < 0.0001) continue;

            //*** snap on grid ?
            if (this.use_grid) {
                var x = this._grid_min_step * Math.round(p[k] / this._grid_min_step);
                var z = (x - edge0[k]) / direction[k];
                var dst = Math.abs(z - z0);
                if (z > 0.01 && z < length - 0.01 && dst < min_distance) {
                    min_distance = dst;
                    res.point = cn_add(edge0, cn_mul(direction, z));
                    res.snap_grid[k] = true;
                    res.snap_previous = false;
                    res.position = z / length;
                    snap = true;
                }
            }

            //*** snap on previous point ?
            if (previous_point == null) continue;
            z = (previous_point[k] - edge0[k]) / direction[k];
            if (z <= 0.01 || z >= length - 0.01) continue;
            dst = Math.abs(z - z0);
            if (dst >= min_distance) continue;
            res.point = cn_add(edge0, cn_mul(direction, z));
            res.snap_grid[0] = false;
            res.snap_grid[1] = false;
            res.snap_previous = true;
            res.position = z / length;
            snap = true;
        }

        var screen_point = this.world_to_screen(res.point);
        if (res.snap_grid[0])
            res.svg += '<line class=\'snap_grid\' x1=\'' + screen_point[0] + '\' y1=\'' + (screen_point[1] - this.snap_screen_distance * 2) + '\' x2=\'' + screen_point[0] + '\' y2=\'' + (screen_point[1] + this.snap_screen_distance * 2) + '\' />';

        if (res.snap_grid[1])
            res.svg += '<line class=\'snap_grid\' x1=\'' + (screen_point[0] - this.snap_screen_distance * 2) + '\' y1=\'' + screen_point[1] + '\' x2=\'' + (screen_point[0] + this.snap_screen_distance * 2) + '\' y2=\'' + screen_point[1] + '\' />';

        if (res.snap_previous) {
            var pp = this.world_to_screen(previous_point);
            res.svg += '<line class=\'snap_previous\' x1=\'' + pp[0] + '\' y1=\'' + pp[1] + '\' x2=\'' + screen_point[0] + '\' y2=\'' + screen_point[1] + '\' />';
        }
        return res;
    }

    //***********************************************************************************
    //**** Mouseover utilities
    //***********************************************************************************
    on_vertex(mouse_world_position, p, pixels = 10) {
        const v0 = this.world_to_screen(mouse_world_position);
        const v1 = this.world_to_screen(p);
        if (!v0 || !v1) return false;
        return cn_dist(v0, v1) < pixels;
    }

    on_edge(mouse_world_position, p0, p1) {
        var dir = cn_sub(p1, p0);
        var length = cn_normalize(dir);
        if (length < 10 * this.screen_to_world_scale) return false;
        var x = cn_dot(cn_sub(mouse_world_position, p0), dir);
        if (x < 0 || x > length) return false;
        var y = cn_dot(cn_sub(mouse_world_position, p0), cn_normal(dir));
        if (Math.abs(y) > 10 * this.screen_to_world_scale) return false;
        return true;
    }

    on_edge_screen(mouse_screen_position, v0, v1) {
        let p0 = this.world_to_screen(v0);
        let p1 = this.world_to_screen(v1);
        if (!p0 || !p1) return false;

        var dir = cn_sub(p1, p0);
        var length = cn_normalize(dir);
        if (length < 1) return cn_manhattan_distance(p0, mouse_screen_position) < 10;
        var x = cn_dot(cn_sub(mouse_screen_position, p0), dir);
        if (x < 0 || x > length) return false;
        var y = cn_dot(cn_sub(mouse_screen_position, p0), cn_normal(dir));
        if (Math.abs(y) > 10) return false;
        return true;
    }

    //***********************************************************************************
    //**** Mouse utilities
    //***********************************************************************************

    pan(dx, dy) {
        this._matrix[0] -= dx * this.screen_to_world_scale;
        this._matrix[1] += dy * this.screen_to_world_scale;
        this._update_matrices();
    }

    wheel(mouse_position, forward) {
        this.pan(0.5 * this._width - mouse_position[0], 0.5 * this._height - mouse_position[1]);
        if (forward)
            this._matrix[2] *= 1.05;
        else
            this._matrix[2] /= 1.05;
        this._update_matrices();
        this.pan(-0.5 * this._width + mouse_position[0], -0.5 * this._height + mouse_position[1]);
    }

    wheel_ratio(mouse_position, ratio) {
        this.pan(0.5 * this._width - mouse_position[0], 0.5 * this._height - mouse_position[1]);
        this._matrix[2] *= ratio;
        this._update_matrices();
        this.pan(-0.5 * this._width + mouse_position[0], -0.5 * this._height + mouse_position[1]);
    }

    //***********************************************************************************
    //**** Draw a measure
    //***********************************************************************************
    /**
     *
     * @param {Array<number>} p0 : first point of the measure, in world coordinates
     * @param {Array<number>} p1 : second point of the measure, in world coordinates
     * @param {Array<number>} sel : if not null, output for the screen coordinates of the actual ends of the measure
     * @param {boolean} selected : highlight on the measure
     * @param {number|boolean} offset : disrance from the acutal measured points to the measure line, in pixels. If boolean, 20 pixels for true.
     * @param {string[]} extra_classes : additional class'es for the measure line and text box
     * @param {string} text : text to be displayed in the measure box
     * @param {boolean} force_display : override camera show measure constraint
     */
    draw_measure(p0, p1, sel = null, selected = false, offset = true, extra_classes = [], text = '', force_display = false) {
        if (this.measure_limit && cn_dist(p0, p1) < this.measure_limit) return "";
        const v0 = this.world_to_screen(p0);
        const v1 = this.world_to_screen(p1);
        if (v0 && v1) {
            const displayed_text = (text !== '') ? text : (cnx_dist(p0, p1) * 100).toFixed(0);
            return this.draw_measure_screen(v0, v1, cnx_dist(p0, p1), sel, selected, offset, extra_classes, text, force_display);
        }
        return "";
    }

    /**
     *
     * @param {Array<number>} p0 : first point of the measure, in world coordinates
     * @param {Array<number>} p1 : second point of the measure, in world coordinates
     * @param {Array<number>} sel : if not null, output for the screen coordinates of the actual ends of the measure
     * @param {boolean} selected : highlight on the measure
     * @param {number|boolean} offset : disrance from the acutal measured points to the measure line, in pixels. If boolean, 20 pixels for true.
     * @param {string[]} extra_classes : additional class'es for the measure line and text box
     * @param {string} text : text to be displayed in the measure box
     */
    draw_force_measure(p0, p1, sel = null, selected = false, offset = true, extra_classes = [], text = '') {
        return this.draw_measure(p0, p1, sel, selected, offset, extra_classes, text, true);
    }


    /**
     *
     * @param {Array<number>} v0 : first point of the measure, in screen coordinates
     * @param {Array<number>} v1 : second point of the measure, in screen coordinates
     * @param {number} distance : actual distance between points
     * @param {Array<number>} sel : if not null, output for the screen coordinates of the actual ends of the measure
     * @param {boolean} selected : highlight on the measure
     * @param {number|boolean} offset : disrance from the acutal measured points to the measure line, in pixels. If boolean, 20 pixels for true.
     * @param {string[]} extra_classes : additional class'es for the measure line and text box
     * @param {string} text : text to be displayed in the measure box
     * @param {boolean} force_display : override camera show measure constraint
     */
    draw_measure_screen(v0, v1, distance, sel = null, selected = false, offset = true, extra_classes = [], text = '', force_display = false) {

        let html = ``;
        if (this.show_measures || force_display) {
            const is_export = extra_classes.includes('exp');
            const extra = extra_classes.join(' ');
            if (!v0.length || !v1.length) return html;
            const scale = this.get_scale("measures");
            if (offset) {
                let o = cn_normal(cn_sub(v1, v0));
                cn_normalize(o);
                let off = (typeof (offset) == 'number') ? offset : 20;
                off *= scale;
                o = cn_mul(o, off);
                v0 = cn_add(v0, o);
                v1 = cn_add(v1, o);
            }
            const text_css_class = [...extra_classes];
            const displayed_text = (text !== '') ? text : distance.toFixed(2);//.replace(/\.?0+$/, '');
            if (!is_export) {
                html += `<line class="dimensionning_line ${extra}" `;
                html += `x1="${v0[0]}" y1="${v0[1]}" `;
                html += `x2="${v1[0]}" y2="${v1[1]}" `;
                html += `/>`;

                if (sel) {
                    if (selected)
                        text_css_class.push('selected');
                    else
                        text_css_class.push('dimensionning_text_selectable');
                    const pp = cn_middle(v0, v1);
                    sel[0] = pp[0];
                    sel[1] = pp[1];
                    if (sel.length >= 3) sel[2] = distance;
                }
                const x = (v0[0] + v1[0]) * 0.5;
                const y = (v0[1] + v1[1]) * 0.5
                const width = displayed_text.length * 9
                html += `<rect class="dimensionning_text_background ${text_css_class.join(' ')}" x="${x - width / 2}" y="${y - 11}" width="${width}" height="20" rx="5" ry="5"/>`;
                html += `<text class="dimensionning_text ${text_css_class.join(' ')}" dominant-baseline="middle" text-anchor="middle" x="${x}" y="${y}">${displayed_text}</text>`;
            } else {
                html += `<line class="measurement_line ${extra}" `;
                html += `x1="${v0[0]}" y1="${v0[1]}" `;
                html += `x2="${v1[0]}" y2="${v1[1]}" `;
                html += `/>`;
                let to = cn_normal(cn_sub(v1, v0));
                cn_normalize(to);
                const offset_segment = cn_mul(to, 2 * scale)
                to = cn_mul(to, 10 * scale);
                const vA0 = cn_add(v0, offset_segment)
                const vA1 = cn_sub(v0, offset_segment)
                const vB0 = cn_add(v1, offset_segment)
                const vB1 = cn_sub(v1, offset_segment)
                html += `<line class="measurement_line ${extra}" `;
                html += `x1="${vA0[0]}" y1="${vA0[1]}" `;
                html += `x2="${vA1[0]}" y2="${vA1[1]}" `;
                html += `/>`;
                html += `<line class="measurement_line ${extra}" `;
                html += `x1="${vB0[0]}" y1="${vB0[1]}" `;
                html += `x2="${vB1[0]}" y2="${vB1[1]}" `;
                html += `/>`;
                v0 = cn_add(v0, to);
                v1 = cn_add(v1, to);
                const x = (v0[0] + v1[0]) * 0.5;
                const y = (v0[1] + v1[1]) * 0.5
                let rotate_angle = Math.atan2(v1[1] - v0[1], v1[0] - v0[0]) * 180 / Math.PI;
                rotate_angle += rotate_angle >= 90 ? -180 : rotate_angle < -90 ? 180 : 0
                html += `<g transform="translate(${x},${y}) scale(${scale}) rotate(${rotate_angle}, 0 , 0)">`;
                const width = displayed_text.length * 9
                html += `<rect class="dimensionning_text_background ${text_css_class.join(' ')}" x="${0 - width / 2}" y="${0 - 11}" width="${width}" height="20" rx="5" ry="5"/>`;
                html += `<text class="measurement_text ${text_css_class.join(' ')}" dominant-baseline="middle" text-anchor="middle"  x="${0}" y="${0}">${displayed_text}</text>`;
                html += "</g>";
            }
        }
        return html;
    }

    static measure_styles(render_style) {
        let html = "";
        html += '.measurement_line { stroke: #6E6E6E; stroke-width: 1; opacity: 0.75; }\n'
        html += '.measurement_text { color: #6E6E6E; font-size: 12; opacity: 0.75; } \n'
        return html;
    }

    /**
     * Returns true is 'screen point' is over the measure.
     * @param {number[]} screen_point
     * @param {number[]} measure
     * @returns {boolean}
     */
    on_measure(screen_point, measure) {
        return (Math.abs(measure[0] - screen_point[0]) <= 30 && Math.abs(measure[1] - screen_point[1]) <= 10);
    }

    //***********************************************************************************
    //**** Draws an angle
    //***********************************************************************************
    /**
     *
     * @param {number[]} point
     * @param {number[]} dir0
     * @param {number[]} dir1
     * @param {cn_angle_sensitive_area} sa0
     * @param {cn_angle_sensitive_area} sa1
     * @returns
     */
    draw_svg_angle(point, dir0, dir1, sa0 = null, sa1 = null) {

        let html = ``;
        if (this.show_angles) {
            let a0 = cn_polar(dir0)[1];
            let a1 = cn_polar(dir1)[1];
            while (a1 < a0) a1 += 2 * Math.PI;
            while (a1 - a0 > 2 * Math.PI) a1 -= 2 * Math.PI;

            const c0 = this.world_to_screen(point);
            const d0 = cn_cart([1, a0]);
            const d1 = cn_cart([1, a1]);

            const base_line_length = 100 * this.screen_to_world_scale;

            let v0 = this.world_to_screen(cn_add(point, cn_cart([base_line_length, a0])));
            html += `<line class="angle_base_line" x1="${c0[0]}" y1="${c0[1]}" x2="${v0[0]}" y2="${v0[1]}" />`;

            let v1 = this.world_to_screen(cn_add(point, cn_cart([base_line_length, a1])));
            html += `<line class="angle_base_line" x1="${c0[0]}" y1="${c0[1]}" x2="${v1[0]}" y2="${v1[1]}" />`;

            const screen_radius = 80;
            const radius = screen_radius * this.screen_to_world_scale;
            v0 = this.world_to_screen(cn_add(point, cn_cart([radius, a0])));
            v1 = this.world_to_screen(cn_add(point, cn_cart([radius, a1])));

            const sense = (a1 - a0 > Math.PI) ? 1 : 0;
            html += `<path class="angle_circle_line" d="M ${v0[0]} ${v0[1]} A ${screen_radius} ${screen_radius} 0 ${sense} 0 ${v1[0]} ${v1[1]}" />`;

            const cc = this.world_to_screen(cn_add(point, cn_cart([radius, (a0 + a1) * 0.5])));
            html += `<circle class="angle_label_fill" cx="${cc[0]}" cy="${cc[1]}" r="20" />`;

            if (sa0 || sa1) {
                let dy = 10;
                let dx = 20;
                let alpha0 = 45 / 80;
                let alpha1 = (45 - dx) / 80;
                let sav = [sa0, sa1];
                for (var ns = 0; ns < 2; ns++) {
                    if (!sav[ns]) continue;
                    let a = (ns == 0) ? -1 : 1;
                    const c0 = this.world_to_screen(cn_add(point, cn_cart([radius, (a0 + a1) * 0.5 + alpha0 * a])));
                    const c1 = this.world_to_screen(cn_add(point, cn_cart([radius + this.screen_to_world_scale * dy, (a0 + a1) * 0.5 + alpha1 * a])));
                    const c2 = this.world_to_screen(cn_add(point, cn_cart([radius - this.screen_to_world_scale * dy, (a0 + a1) * 0.5 + alpha1 * a])));
                    html += `<path class="angle_label_fill ${(sav[ns].mouseover) ? "mouseover" : (sav[ns].selected) ? "selected" : ""} " d="M ${c0[0]} ${c0[1]} L ${c1[0]} ${c1[1]} ${c2[0]} ${c2[1]} Z" />`;
                    sav[ns].screen = cn_mul(cn_add(c0, cn_add(c1, c2)), 1 / 3);
                    sav[ns].screen.push(dx);
                    sav[ns].angles[ns] = a0;
                    sav[ns].angles[1 - ns] = a1;
                }
            }

            html += `<text class="angle_label_text" x="${cc[0]}" y="${cc[1]}">${((a1 - a0) * 180 / Math.PI).toFixed(1)} °</text>`;
        }
        return html;
    }

    //***********************************************************************************
    //**** draw camembert
    //***********************************************************************************
    draw_camembert(center, a0, a1, radius, text, graph_class, text_class) {
        var html = '';
        var cc = this.world_to_screen(center);
        var v0 = this.world_to_screen(cn_add(center, cn_cart([radius * this.screen_to_world_scale, a0])));
        var v1 = this.world_to_screen(cn_add(center, cn_cart([radius * this.screen_to_world_scale, a1])));
        var sense = (a1 - a0 > Math.PI) ? 1 : 0;
        html += '<path class=\'' + graph_class + '\' d=\'M ' + cc[0] + ' ' + cc[1] + ' L ' + v0[0] + ' ' + v0[1] + ' A ' + radius + ' ' + radius + ' 0 ' + sense + ' 0 ' + v1[0] + ' ' + v1[1] + ' Z\' />';

        if (text !== '') {
            var p = this.world_to_screen(cn_add(center, cn_cart([radius * this.screen_to_world_scale * 0.8, (a0 + a1) * 0.5])));
            html += '<text class=\'' + text_class + '\' x=\'' + p[0] + '\' y=\'' + p[1] + '\'>' + text + '</text>';
        }

        return html;
    }

    //***********************************************************************************
    //**** draw text box
    //***********************************************************************************
    draw_text_box(center, w, h, text, graph_class, text_class) {
        var html = '';

        var p = this.world_to_screen(center);
        html += '<rect class=\'' + graph_class + '\' x=\'' + (p[0] - w / 2) + '\' y=\'' + (p[1] - h / 2) + '\' width=\'' + w + '\' height=\'' + h + '\' />';
        html += '<text class=\'' + text_class + '\' x=\'' + p[0] + '\' y=\'' + p[1] + '\'>' + text + '</text>';
        return html;
    }

    draw_text_box_screen(p, w, h, text, graph_class, text_class) {
        var html = '';
        html += '<rect class=\'' + graph_class + '\' x=\'' + (p[0] - w / 2) + '\' y=\'' + (p[1] - h / 2) + '\' width=\'' + w + '\' height=\'' + h + '\' />';
        html += '<text class=\'' + text_class + '\' x=\'' + p[0] + '\' y=\'' + p[1] + '\'>' + text + '</text>';
        return html;
    }

    draw_text_box_left_screen(position, w, h, text, graph_class, text_class) {
        var html = '';

        html += `<rect class="${graph_class}" x="${position[0]}" y="${position[1] - h / 2}" width="${w}" height="${h}" />`;
        html += `<text class="${text_class}" dominant-baseline="central" text-anchor="start" x="${position[0] + 10}" y="${position[1]}">${text}</text>`;
        return html;
    }

    //***********************************************************************************
    //**** Update matrices
    //***********************************************************************************
    _update_matrices() {
        var diagonal = Math.sqrt(this._width * this._width + this._height * this._height);
        if (diagonal < 1) diagonal = 1;
        this.screen_to_world_scale = this._matrix[2] / diagonal;
        this._screen_to_world[0] = this._matrix[0] - this.screen_to_world_scale * 0.5 * this._width;
        this._screen_to_world[1] = this._matrix[1] - this.screen_to_world_scale * 0.5 * this._height;

        this.world_to_screen_scale = diagonal / this._matrix[2];
        this._world_to_screen[0] = 0.5 * this._width - this._matrix[0] * this.world_to_screen_scale;
        this._world_to_screen[1] = 0.5 * this._height - this._matrix[1] * this.world_to_screen_scale;

        this.snap_world_distance = (this._width + this._height) * SNAP_RATIO * this.screen_to_world_scale;
        if (this.snap_world_distance < 0.01) this.snap_world_distance = 0.01;
        this.snap_screen_distance = (this._width + this._height) * SNAP_RATIO;
        if (this.snap_screen_distance < 10) this.snap_screen_distance = 10;
    }

    //***********************************************************************************
    //**** Draw a line
    //***********************************************************************************
    draw_line(v0, v1, classes = '') {
        var p0 = this.world_to_screen(v0);
        var p1 = this.world_to_screen(v1);
        if (!p0 || !p1) return '';
        return '<line class=\'' + classes + '\' x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' />';
    }

    /**
     * Draws 4 arrows around given point
     * @param {number[]} v : point in world coordinates
     * @param {string} classes : draw classes
     * @returns {string} returns svg element
     */
    draw_move_arrow(v, classes = '') {
        return this.draw_move_arrow_screen(this.world_to_screen(v), classes);
    }

    /**
     * Draws 4 arrows around given point
     * @param {number[]} v : point in screen coordinates
     * @param {string} classes : draw classes
     * @returns {string} returns svg element
     */
    draw_move_arrow_screen(v, classes = '') {
        const x = v[0];
        const y = v[1];
        const rect_width = 4;
        const padding = 8;
        const rect_length = 20;
        let svg = '';
        svg += `<path class="move_arrow ${classes}" d="M ${x - rect_width / 2} ${y + padding} V ${y + padding + rect_length} H ${x + rect_width / 2} V ${y + padding} Z" />`;
        svg += `<path class="move_arrow ${classes}" d="M ${x - rect_width / 2} ${y - padding} V ${y - padding - rect_length} H ${x + rect_width / 2} V ${y - padding} Z" />`;
        svg += `<path class="move_arrow ${classes}" d="M ${x + padding} ${y - rect_width / 2} H ${x + padding + rect_length} V ${y + rect_width / 2} H ${x + padding} Z" />`;
        svg += `<path class="move_arrow ${classes}" d="M ${x - padding} ${y - rect_width / 2} H ${x - padding - rect_length} V ${y + rect_width / 2} H ${x - padding} Z" />`;
        return svg;
    }

    /**
     * returns true if mouse is over the point
     * @param {number[]} mouse_world
     * @param {number[]} point
     * @returns {boolean}
     */
    move_arrow_selected(mouse_world, point) {
        return cn_dist(mouse_world, point) < this.snap_world_distance * 3;
    }

    /**
     * returns true if mouse is over the point
     * @param {number[]} mouse_screen
     * @param {number[]} point
     * @returns {boolean}
     */
    move_arrow_selected_screen(mouse_screen, point) {
        if (!point || !mouse_screen) return false;
        return cn_dist(mouse_screen, point) < this.snap_screen_distance * 3;
    }

    /**
     * Draws a cross around given point
     * @param {number[]} v : point in world coordinates
     * @returns {string} returns svg element
     */
    draw_scissor(v) {
        const p0 = this.world_to_screen(v);
        const x = p0[0];
        const y = p0[1];
        const e = 15
        let svg = '';
        svg += `<line x1='${x - e}' x2="${x + e}" y1="${y - e}" y2="${y + e}" stroke="red" stroke-dasharray="4" stroke-width="2"/>`;
        svg += `<line x1='${x - e}' x2="${x + e}" y1="${y + e}" y2="${y - e}" stroke="red" stroke-dasharray="4" stroke-width="2"/>`;
        return svg;
    }

    /**
     * Draws 4 arrows around given point
     * @param {number[]} v0 : point 0 in world coordinates
     * @param {number[]} v1 : point 1 in world coordinates
     * @param {string} classes : draw classes
     * @returns {string} returns svg element
     */
    draw_line_move_arrow_old(v0, v1, classes = '') {
        var p0 = this.world_to_screen(cn_middle(v0, v1));
        var x = p0[0];
        var y = p0[1];
        var sz0 = 15;
        var sz1 = 40;
        var dsz = 12;
        var svg = '';
        svg += `<g transform="translate(${x},${y}) rotate(${cn_polar(cn_sub(this.world_to_screen(v0), this.world_to_screen(v1)))[1] * 180 / Math.PI}) ">`;
        svg += `<path class="move_arrow ${classes}" d="M ${-dsz}  ${sz0} L 0 ${sz1} ${dsz} ${sz0} Z" />`;
        svg += `<path class="move_arrow ${classes}" d="M ${-dsz}  ${-sz0} L 0 ${-sz1} ${dsz} ${-sz0} Z" />`;
        svg += `</g>`;
        return svg;
    }

    /**
     * Draws 4 arrows around given point
     * @param {number[]} v0 : point 0 in world coordinates
     * @param {number[]} v1 : point 1 in world coordinates
     * @param {string} classes : draw classes
     * @returns {string} returns svg element
     */
    draw_line_move_arrow_screen_old(v0, v1, classes = '') {
        var p0 = cn_middle(v0, v1);
        var x = p0[0];
        var y = p0[1];
        var sz0 = 15;
        var sz1 = 40;
        var dsz = 12;
        var svg = '';
        svg += `<g transform="translate(${x},${y}) rotate(${cn_polar(cn_sub(v0, v1))[1] * 180 / Math.PI}) ">`;
        svg += `<path class="move_arrow ${classes}" d="M ${-dsz}  ${sz0} L 0 ${sz1} ${dsz} ${sz0} Z" />`;
        svg += `<path class="move_arrow ${classes}" d="M ${-dsz}  ${-sz0} L 0 ${-sz1} ${dsz} ${-sz0} Z" />`;
        svg += `</g>`;
        return svg;
    }

    /**
     * Draws 4 arrows around given point
     * @param {number[]} v0 : point 0 in world coordinates
     * @param {number[]} v1 : point 1 in world coordinates
     * @param {string} classes : draw classes
     * @returns {string} returns svg element
     */
    draw_line_move_arrow_screen(v0, v1, classes = '') {
        var p0 = cn_middle(v0, v1);
        var x = p0[0];
        var y = p0[1];
        var sz0 = 5;
        var sz1 = 30;
        var dsz = 12;
        var svg = '';
        let l = 0.5 * cn_dist(v0, v1);

        svg += `<g transform="translate(${x},${y}) rotate(${cn_polar(cn_sub(v0, v1))[1] * 180 / Math.PI}) ">`;
        svg += `<path class="move_arrow ${classes}" d="M ${-dsz}  ${sz0} L 0 ${sz1} ${dsz} ${sz0} ${l} ${sz0} ${-l} ${sz0} Z" />`;
        svg += `<path class="move_arrow ${classes}" d="M ${-dsz}  ${-sz0} L 0 ${-sz1} ${dsz} ${-sz0}  ${l} ${-sz0} ${-l} ${-sz0} Z" />`;
        svg += `</g>`;
        return svg;
    }

    draw_line_move_arrow(v0, v1, classes = '') {
        return this.draw_line_move_arrow_screen(this.world_to_screen(v0), this.world_to_screen(v1), classes);
    }


    /**
     * Draws a selectable arrow head
     * @param {Array<number>} p0 : origin of arrow, in screen coordinates
     * @param {Array<number>} dir : direction of arrow, in screen coordinates
     * @param {string} classes : additional classees
     * @returns
     */
    draw_arrow_head(p0, dir, classes = '') {
        const nor = cn_mul(cn_normal(dir), 10);
        const ddir = cn_mul(dir, 30);
        var svg = '';
        svg += `<path class="selectable_arrow_head ${classes}" d="M ${p0[0] + ddir[0]}  ${p0[1] + ddir[1]} L ${p0[0] + nor[0]}  ${p0[1] + nor[1]} ${p0[0] - nor[0]}  ${p0[1] - nor[1]} Z" />`;
        return svg;
    }

    /**
     * returns true if mouse id over the arrow
     * @param {Array<number>} screen_mouse : mouseposition, in screen coordinates
     * @param {Array<number>} p0 : origin of arrow, in screen coordinates
     * @param {Array<number>} dir : direction of arrow, in screen coordinates
     * @returns
     */
    mouse_over_arrow_head(screen_mouse, p0, dir) {
        const nor = cn_normal(dir);
        const d = cn_sub(screen_mouse, p0);
        const x = cn_dot(d, dir);
        if (x < 0 || x > 30) return false;
        const y = cn_dot(d, nor);
        if (Math.abs(y) > 10) return false;
        return true;
    }

    /**
     * Draw a path
     * @param {Array<number[]>} vertices
     * @param {string} classes
     * @param {boolean} closed
     */
    draw_path(vertices, classes, closed = false, fill = "") {
        var svg = '';
        if (vertices.length < 2) return svg;
        svg += `<path class="${classes}" ${fill} d="`;

        vertices.forEach((v, i) => {
            const p = this.world_to_screen(v);
            if (!p) return '';
            if (i == 0) svg += 'M ';
            if (i == 1) svg += 'L ';
            svg += `${p[0]} ${p[1]} `;
        });
        if (closed) svg += 'Z';
        svg += `" />`;
        return svg;
    }

    /**
     * Draw a path
     * @param {Array<number[]>} vertices
     * @param {string} classes
     * @param {boolean} closed
     */
    draw_path_screen(vertices, classes, closed = false, fill = "") {
        var svg = '';
        if (vertices.length < 2) return svg;
        svg += `<path class="${classes}" ${fill} d="`;

        vertices.forEach((p, i) => {
            if (!p) return '';
            if (i == 0) svg += 'M ';
            if (i == 1) svg += 'L ';
            svg += `${p[0]} ${p[1]} `;
        });
        if (closed) svg += 'Z';
        svg += `" />`;
        return svg;
    }

    //***********************************************************************************
    /**
     * Draws a polygon in SVG.
     * @param {fh_polygon} polygon
     * @param {string} classes
     * @param {number} zoffset
     */
    draw_polygon(polygon, classes = '', zoffset = 0) {
        var svg = '';
        polygon.compute_contours();
        if (polygon.contour_sizes.length == 0) return svg;
        /*if (polygon.contour_sizes.length > 1) {
            polygon.contour_vertices = cn_polygon_to_single_contour(polygon);
            polygon.contour_sizes = [polygon.contour_vertices.length];
        }*/
        svg += `<path class="${classes}" d="`;
        var offset = 0;
        for (var nc = 0; nc < polygon.contour_sizes.length; nc++) {
            var sz = polygon.contour_sizes[nc];
            for (var nv = 0; nv < sz; nv++) {
                if (nv == 0) svg += 'M ';
                else if (nv == 1) svg += 'L ';
                const pv = polygon.contour_vertices[offset + nv];
                var p = this.world_to_screen([pv[0], pv[1], pv[2] + zoffset]);
                if (!p.length) return '';
                svg += `${p[0]} ${p[1]} `;
            }
            svg += `Z `;

            offset += sz;
        }
        svg += '"  fill-rule="evenodd" />';

        return svg;
    }

    //***********************************************************************************
    /**
     * Draws a polygon in SVG.
     * @param {fh_polygon} polygon
     * @param {string} style
     * @param {number} zoffset
     */
    draw_styled_polygon(polygon, style = '', zoffset = 0) {
        var svg = '';
        polygon.compute_contours();
        if (polygon.contour_sizes.length == 0) return svg;
        /*if (polygon.contour_sizes.length > 1) {
            polygon.contour_vertices = cn_polygon_to_single_contour(polygon);
            polygon.contour_sizes = [polygon.contour_vertices.length];
        }*/
        svg += `<path style="${style}" d="`;
        var offset = 0;
        for (var nc = 0; nc < polygon.contour_sizes.length; nc++) {
            var sz = polygon.contour_sizes[nc];
            for (var nv = 0; nv < sz; nv++) {
                if (nv == 0) svg += 'M ';
                else if (nv == 1) svg += 'L ';
                const pv = polygon.contour_vertices[offset + nv];
                var p = this.world_to_screen([pv[0], pv[1], pv[2] + zoffset]);
                if (!p.length) return '';
                svg += `${p[0]} ${p[1]} `;
            }
            svg += `Z `;

            offset += sz;
        }
        svg += '"  fill-rule="evenodd" />';

        return svg;
    }
    //***********************************************************************************

    //***********************************************************************************
    /**
     * Draws a solid in SVG.
     * @param {fh_solid} solid
     * @param {string} classes
     * @param {number} zoffset
     */
    draw_solid(solid, classes = '', zoffset = 0) {
        var svg = '';
        const faces = solid.get_faces();
        faces.forEach(f => svg += this.draw_polygon(f, classes, zoffset));
        return svg;
    }

    /**
     * Draws a 3D circle from 3D world coordinates
     * @param {Array<number>} center
     * @param {number} radius
     * @param {Array<number>} normal
     * @param {string} classes
     * @param {number} nb_samples
     * @returns
     */
    draw_3d_circle(center, radius, normal, classes = '', nb_samples = 32) {
        const dx = [0, 0, 0];
        const dy = [0, 0, 0];
        cnx_build_axis(normal, dx, dy);
        var svg = '';
        svg += `<path class="${classes}" d="`;
        for (var s = 0; s < nb_samples; s++) {
            const theta = 2 * Math.PI * s / nb_samples;
            const p = cnx_add(center, cnx_mul(cnx_add(cnx_mul(dx, Math.cos(theta)), cnx_mul(dy, Math.sin(theta))), radius));
            const sp = this.world_to_screen(p);
            if (!sp.length) return '';
            if (s == 0) svg += 'M ';
            else if (s == 1) svg += 'L ';
            svg += `${sp[0]} ${sp[1]} `;
        }
        svg += `Z" />`;
        return svg;
    }

    draw_lock(world_position, locked, selected, size = 40) {
        let html = "";

        let p = this.world_to_screen(world_position);
        const svg = (locked) ? 'lock.svg' : 'unlock.svg';
        let r0 = size;
        let r1 = size;
        html += `<image opacity="${selected ? 1 : 0.5}" xlink:href="${cn_image_dir() + svg}" x=" ${p[0] - r0 / 2}" y="${p[1] - r1 / 2}" width="${r0}" height="${r1}" />`;

        return html;
    }

    /**
     * Draws a line using textures attached to the element
     * @param {string} element_id
     * @param {number[]} pp0 - First point in world coordinates
     * @param {number[]} pp1 - Second point in world coordinates
     * @returns {string}
     */
    draw_textured_line(element_id, pp0, pp1, centered = false) {
        let svg = "";
        let textures = this._svg_context.get_textures_per_element(element_id);
        if (!textures.length) return svg;
        let p0 = this.world_to_screen(pp0);
        let p1 = this.world_to_screen(pp1);
        if (cn_dist(p0, p1) < 10)
            return this.draw_textured_point(element_id, cn_middle(pp0, pp1));

        let delta = cn_normal(cn_sub(p1, p0));
        cn_normalize(delta);
        let stroke_width = 5;
        let stroke_delta = 2;
        if (centered) {
            let full_width = -stroke_width + textures.length * stroke_width + (textures.length - 1) * stroke_delta;
            let dd = cn_mul(delta, 0.5 * full_width);
            p0 = cn_sub(p0, dd);
            p1 = cn_sub(p1, dd);
        }
        delta = cn_mul(delta, stroke_width + stroke_delta);
        textures.forEach(t => {
            svg += `<line style="stroke: ${t.color}; stroke-dasharray: ${t.dasharray == "" ? 0 : t.dasharray}; stroke-width: ${stroke_width}" x1="${p0[0]}" y1="${p0[1]}" x2="${p1[0]}" y2="${p1[1]}" />`;
            p0 = cn_add(p0, delta);
            p1 = cn_add(p1, delta);
        });
        return svg;
    }

    /**
     * Draws a line using textures attached to the element
     * @param {string} element_id
     * @param {number[]} pp
     * @returns {string}
     */
    draw_textured_point(element_id, pp) {
        let svg = "";
        let textures = this._svg_context.get_textures_per_element(element_id);
        if (!textures.length) return svg;
        let p = this.world_to_screen(pp);
        let stroke_width = 5;
        let stroke_delta = 2;
        let radius = 15;
        textures.forEach(t => {
            svg += `<circle style="stroke: ${t.color}; stroke-width: ${stroke_width}; stroke-dasharray: ${t.dasharray || 0}; fill: none" cx="${p[0]}" cy="${p[1]}" r="${radius}" />`;
            radius += stroke_width + stroke_delta;
        });
        return svg;
    }

    /**
     * Draws a line using textures attached to the element
     * @param {string} element_id
     * @param {number[][]} path
     * @returns {string}
     */
    draw_textured_path(element_id, path) {
        let svg = "";
        let textures = this._svg_context.get_textures_per_element(element_id);
        if (!textures.length) return svg;

        /*let p0 = this.world_to_screen(pp0);
        let p1 = this.world_to_screen(pp1);
        let delta = cn_normal(cn_sub(p1,p0));
        cn_normalize(delta);
        let stroke_width = Math.floor(this._svg_context.texture_line_threshold / textures.length);
        if (stroke_width < 5) stroke_width = 5;
        stroke_width = 3;
        delta = cn_mul(delta, stroke_width);
        textures.forEach(t => {
            svg += `<line style="stroke: ${t.color}; stroke-dasharray: ${t.dasharray == "" ? 0 : t.dasharray}; stroke-width: ${stroke_width}" x1="${p0[0]}" y1="${p0[1]}" x2="${p1[0]}" y2="${p1[1]}" />`;
            p0 = cn_add(p0,delta);
            p1 = cn_add(p1,delta);
        });*/
        return svg;
    }

    _build_pattern(textures) {
        textures = textures.filter((txt, i, ref) => ref.findIndex(oth => oth.color === txt.color && oth.texture === txt.texture) === i);
        textures.sort((a, b) => a.color.localeCompare(b.color) || a.texture.localeCompare(b.texture));
        const texture_id = textures.map(m => m.color.slice(1) + '_' + m.texture).join('-');
        if (this._patterns[texture_id]) return;
    }
}

